<template>
  <div class="container">
    <div class="dropdown-container">
      <div class="dropdown-item">
        <label for="distributor">Select Distributor</label>
        <select id="distributor" v-model="filters.distributor" :disabled="isDistributorLocked || isBusy">
          <option value="">All Distributors</option>
          <option v-for="d in distributorList" :key="d.profile_id ?? d.id" :value="String(d.profile_id ?? d.id)">
            {{ d.business_name || d.business_display_name || d.name || ("Distributor #" + (d.profile_id ?? d.id)) }}
          </option>
        </select>
      </div>

      <div class="dropdown-item">
        <label for="startDate">Start Date</label>
        <input type="date" id="startDate" v-model="filters.startDate" :disabled="isBusy" />
      </div>

      <div class="dropdown-item">
        <label for="endDate">End Date</label>
        <input type="date" id="endDate" v-model="filters.endDate" :disabled="isBusy" />
      </div>

      <div class="dropdown-item">
        <label for="returnNo">Return No</label>
        <input type="text" id="returnNo" v-model="filters.returnNo" placeholder="Enter Return No" :disabled="isBusy" />
      </div>

      <div class="dropdown-item search-container">
        <button @click="performSearch" class="btn button-default" :disabled="isBusy">
          <i v-if="isBusy" class="fa fa-spinner fa-spin"></i>
          {{ isBusy ? "Searching..." : "Search" }}
        </button>
      </div>

      <div class="dropdown-item search-container">
        <button @click="exportToExcel" class="btn button-export" :disabled="!reportData.length || isBusy">
          Export to Excel
        </button>
      </div>
    </div>

    <div v-if="isBusy" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i>
      <span v-if="loadingInit"> Loading distributors...</span>
      <span v-else> Loading data...</span>
    </div>

    <div v-if="!isBusy && reportData.length" class="card mt-3">
      <div class="table-scroll">
        <table class="table table-striped table-bordered table-hover">
          <thead>
            <tr>
              <th rowspan="3" class="align-middle">Return No</th>
              <th rowspan="3" class="align-middle">Dealer Name</th>
              <th rowspan="3" class="align-middle">Return Date</th>
              <th rowspan="3" class="align-middle">Return Reason</th>
              <th rowspan="3" class="align-middle text-end">Total Return Qty</th>
              <th rowspan="3" class="align-middle text-end">Total Return Amount</th>
              <th rowspan="3" class="align-middle text-end">Total VAT Return Amount</th>
              <th rowspan="3" class="align-middle">Created By</th>
              
              <th 
                v-for="itemName in uniqueItemNames" 
                :key="itemName" 
                colspan="10" 
                class="text-center group-header item-divider-left">
                {{ itemName }}
              </th>
            </tr>
            <tr>
              <template v-for="itemName in uniqueItemNames" :key="itemName + '-type-headers'">
                <th rowspan="2" class="text-end sub-header item-divider-left align-middle">Item Price</th>
                <th rowspan="2" class="text-end sub-header align-middle">Total Price</th>
                <th rowspan="2" class="text-end sub-header align-middle">VAT Amount</th>
                <th rowspan="2" class="text-end sub-header align-middle">Total VAT Amount</th>
                <th colspan="2" class="text-center sub-header auto-header">AUTOMATIC</th>
                <th colspan="2" class="text-center sub-header other-header">OTHER</th>
                <th colspan="2" class="text-center sub-header trust-header">TRUST</th>
              </template>
            </tr>
            <tr>
              <template v-for="itemName in uniqueItemNames" :key="itemName + '-qty-headers'">
                <th class="text-end sub-header auto-header">Normal Qty</th>
                <th class="text-end sub-header auto-header">Damaged Qty</th>
                <th class="text-end sub-header other-header">Normal Qty</th>
                <th class="text-end sub-header other-header">Damaged Qty</th>
                <th class="text-end sub-header trust-header">Normal Qty</th>
                <th class="text-end sub-header trust-header">Damaged Qty</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in reportData" :key="i">
              <td class="fw-bold">{{ row.return_no || "-" }}</td>
              <td>{{ row.dealer_name || "-" }}</td>
              <td>{{ row.return_date || "-" }}</td>
              <td>{{ row.return_reason || "-" }}</td>
              <td class="text-end">{{ formatQty(row.total_return_qty) }}</td>
              <td class="text-end">{{ formatNumber(row.total_return_amount) }}</td>
              <td class="text-end">{{ formatNumber(row.total_vat_return_amount) }}</td>
              <td>{{ row.created_by_name || row.created_by || "-" }}</td>
              
              <template v-for="itemName in uniqueItemNames" :key="itemName + '-data'">
                <td class="text-end item-divider-left">{{ formatNumber(getItemValue(row, itemName, null, 'item_price')) }}</td>
                <td class="text-end">{{ formatNumber(getItemValue(row, itemName, null, 'total_price')) }}</td>
                <td class="text-end">{{ formatNumber(getItemValue(row, itemName, null, 'vat_amount')) }}</td>
                <td class="text-end">{{ formatNumber(getItemValue(row, itemName, null, 'total_vat_amount')) }}</td>
                <td class="text-end auto-cell">{{ formatQty(getItemValue(row, itemName, 'AUTOMATIC', 'normal_qty')) }}</td>
                <td class="text-end auto-cell">{{ formatQty(getItemValue(row, itemName, 'AUTOMATIC', 'damaged_qty')) }}</td>
                <td class="text-end other-cell">{{ formatQty(getItemValue(row, itemName, 'OTHER', 'normal_qty')) }}</td>
                <td class="text-end other-cell">{{ formatQty(getItemValue(row, itemName, 'OTHER', 'damaged_qty')) }}</td>
                <td class="text-end trust-cell">{{ formatQty(getItemValue(row, itemName, 'TRUST', 'normal_qty')) }}</td>
                <td class="text-end trust-cell">{{ formatQty(getItemValue(row, itemName, 'TRUST', 'damaged_qty')) }}</td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!isBusy && searchedOnce && !reportData.length" class="no-data-message">
      <p>No data found. Please adjust filters and click Search.</p>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { useToast } from "vue-toastification";
import { fetchDistributors, fetchStockReturnReport } from "@/services/dataService"; 

export default {
  name: "ReturnReportForm",
  data() {
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      token: localStorage.getItem("token_set") || "",
      profileId: Number(localStorage.getItem("profile_id") || 0),
      
      filters: {
        distributor: "",
        startDate: currentDate, 
        endDate: currentDate,   
        returnNo: ""
      },
      
      distributorList: [],
      isDistributorLocked: false,
      
      loadingInit: false,
      isBusy: false,
      searchedOnce: false,
      
      reportData: [], 
      uniqueItemNames: [] 
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  mounted() {
    this.init();
  },
  methods: {
    // ===== Initialization & API =====
    async init() {
      this.loadingInit = true;
      this.isBusy = true;
      try {
        if (!this.token) {
          this.toast.error("Session expired. Please log in again.");
          return;
        }
        await this.loadDistributors();
      } finally {
        this.loadingInit = false;
        this.isBusy = false;
      }
    },

    async loadDistributors() {
      try {
        const res = await fetchDistributors(this.profileId, this.token);
        if (res?.response?.status === 401) {
          this.toast.error("Unauthorized. Please log in again.");
          return;
        }
        const body = res?.data ?? res;
        const listRaw = body?.data || body || [];
        
        this.distributorList = (Array.isArray(listRaw) ? listRaw : []).filter(d => {
          const active = d?.is_active ?? d?.user_is_active ?? d?.app_user_is_active ?? d?.active;
          if (active === undefined || active === null) return true;
          return active === true || Number(active) === 1;
        });

        const userLabel = String(localStorage.getItem("user_label") || "").toUpperCase().trim();
        if (userLabel === "DISTRIBUTOR" || userLabel.startsWith("DIS")) {
          this.filters.distributor = String(this.distributorList[0]?.profile_id);
          this.isDistributorLocked = true;
        }
      } catch (err) {
        console.error("Failed to load distributors:", err);
        this.toast.error("Failed to load distributor list.");
      }
    },

    // Helper to extract specific item values based on the new API format (item_name)
    getItemValue(row, itemName, returnType, fieldName) {
      if (!row.items || !Array.isArray(row.items)) return 0;
      
      if (returnType === null || returnType === undefined || returnType === "") {
        if (fieldName === "price" || fieldName === "item_price" || fieldName === "vat_amount") {
          const found = row.items.find(item => item.item_name === itemName);
          if (!found) return 0;
          if (fieldName === "vat_amount") return Number(found.vat_amount) || 0;
          return Number(found.item_price || found.price) || 0;
        }
        // Sum the requested field across all return types for this item
        const matchingItems = row.items.filter(item => item.item_name === itemName);
        return matchingItems.reduce((sum, item) => sum + (Number(item[fieldName]) || 0), 0);
      }
      
      const matchingItems = row.items.filter(
        item => item.item_name === itemName && String(item.return_type).toUpperCase() === String(returnType).toUpperCase()
      );
      
      return matchingItems.reduce((sum, item) => sum + (Number(item[fieldName]) || 0), 0);
    },

    formatQty(v) {
      if (v === undefined || v === null || v === "") return "0";
      const n = Number(v);
      if (isNaN(n)) return "0";
      return n % 1 === 0 ? String(n) : n.toFixed(2);
    },

    // ===== Search API & Export =====
    async performSearch() {
      this.isBusy = true;
      this.searchedOnce = false;
      this.reportData = [];
      this.uniqueItemNames = [];

      try {
        // Construct API Payload
        const payload = {
          distributor_id: this.filters.distributor ? Number(this.filters.distributor) : null,
          start_date: this.filters.startDate,
          end_date: this.filters.endDate,
          return_no: this.filters.returnNo || null
        };

        const res = await fetchStockReturnReport(this.token, payload);
        
        if (res?.response?.status === 401) {
          this.toast.error("Unauthorized. Please log in again.");
          return;
        }

        const body = res?.data ?? res;

        if (body?.status === "success" || body?.status === true) {
          const rawData = body.data || [];
          
          // 1. Extract all unique item names to build dynamic headers
          const allNames = new Set();
          rawData.forEach(rtn => {
            if (rtn.items && Array.isArray(rtn.items)) {
              rtn.items.forEach(item => {
                if(item.item_name) allNames.add(item.item_name);
              });
            }
          });
          
          this.uniqueItemNames = Array.from(allNames).sort();
          this.reportData = rawData;

          if(this.reportData.length > 0) {
            this.toast.success("Data fetched successfully.");
          }
        } else {
          this.toast.error(body?.message || "Failed to fetch report data.");
        }

      } catch (err) {
        console.error("Error fetching report:", err);
        this.toast.error(err?.response?.data?.message || "An error occurred while fetching the report.");
      } finally {
        this.isBusy = false;
        this.searchedOnce = true;
      }
    },

    // Export to Excel
    exportToExcel() {
      if (!this.reportData.length) {
        this.toast.error("No data to export.");
        return;
      }

      // ---------------- Determine Total Columns & Pad Function ----------------
      // Base cols (8) + (Number of unique items * 10 sub-columns per item)
      const fullCols = 8 + (this.uniqueItemNames.length * 10);
      const padTo = (arr) => {
        const out = Array.isArray(arr) ? [...arr] : [];
        while (out.length < fullCols) out.push("");
        return out.slice(0, fullCols);
      };

      // ---------------- FILTERS (Top Metadata) ----------------
      // Find selected distributor name
      let distributorName = "All Distributors";
      if (this.filters.distributor) {
        const selectedDist = this.distributorList.find(d => String(d.profile_id ?? d.id) === String(this.filters.distributor));
        if (selectedDist) {
          distributorName = selectedDist.business_name || selectedDist.business_display_name || selectedDist.name || `Distributor #${this.filters.distributor}`;
        }
      }

      const filterRows = [
        ["Stock Return Report"],
        [],
        ["Distributor", distributorName],
        ["Start Date", this.filters.startDate || "-"],
        ["End Date", this.filters.endDate || "-"],
        ["Return No", this.filters.returnNo || "-"],
        [],
      ];

      // ---------------- HEADERS ----------------
      const headerRow1 = [
        "Return No", "Dealer Name", "Return Date", "Return Reason", 
        "Total Return Qty", "Total Return Amount", "Total VAT Return Amount", "Created By"
      ];
      const headerRow2 = ["", "", "", "", "", "", "", ""]; 
      const headerRow3 = ["", "", "", "", "", "", "", ""]; 

      this.uniqueItemNames.forEach(itemName => {
        headerRow1.push(itemName, "", "", "", "", "", "", "", "", ""); // Span 10 cols
        headerRow2.push("Item Price", "Total Price", "VAT Amount", "Total VAT Amount", "AUTOMATIC", "", "OTHER", "", "TRUST", "");
        headerRow3.push("", "", "", "", "Normal Qty", "Damaged Qty", "Normal Qty", "Damaged Qty", "Normal Qty", "Damaged Qty");
      });

      // ---------------- DATA ROWS ----------------
      const dataRows = this.reportData.map((row) => {
        const rowData = [
          row.return_no || "",
          row.dealer_name || "",
          row.return_date || "",
          row.return_reason || "",
          Number(row.total_return_qty) || 0,
          Number(row.total_return_amount) || 0,
          Number(row.total_vat_return_amount) || 0,
          row.created_by_name || row.created_by || ""
        ];

        this.uniqueItemNames.forEach(itemName => {
          rowData.push(
            Number(this.getItemValue(row, itemName, null, 'item_price')) || 0,
            Number(this.getItemValue(row, itemName, null, 'total_price')) || 0,
            Number(this.getItemValue(row, itemName, null, 'vat_amount')) || 0,
            Number(this.getItemValue(row, itemName, null, 'total_vat_amount')) || 0,
            Number(this.getItemValue(row, itemName, 'AUTOMATIC', 'normal_qty')) || 0,
            Number(this.getItemValue(row, itemName, 'AUTOMATIC', 'damaged_qty')) || 0,
            Number(this.getItemValue(row, itemName, 'OTHER', 'normal_qty')) || 0,
            Number(this.getItemValue(row, itemName, 'OTHER', 'damaged_qty')) || 0,
            Number(this.getItemValue(row, itemName, 'TRUST', 'normal_qty')) || 0,
            Number(this.getItemValue(row, itemName, 'TRUST', 'damaged_qty')) || 0
          );
        });

        return padTo(rowData);
      });

      // ---------------- GENERATED INFO (Bottom Metadata) ----------------
      const firstName = localStorage.getItem("first_name") || "";
      const lastName = localStorage.getItem("last_name") || "";
      const userFullName = (firstName + " " + lastName).trim() || "-";
      const generatedAt = new Date().toLocaleString("en-US", { hour12: false });

      const footerRows = [
        [],
        ["Generated by", userFullName],
        ["Generated at", generatedAt]
      ];

      // ---------------- BUILD FINAL AOA ----------------
      const aoa = [
        ...filterRows.map(padTo),
        padTo(headerRow1),
        padTo(headerRow2),
        padTo(headerRow3),
        ...dataRows,
        ...footerRows.map(padTo)
      ];

      // ---------------- APPLY MERGES ----------------
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const merges = [];
      
      // Because we added filter rows at the top, our table header starts at this index
      const headerStartIndex = filterRows.length; 

      // Merge Main Title
      merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } });

      // Merge base column headers vertically (e.g., Return No spans 3 rows)
      for (let i = 0; i < 8; i++) {
        merges.push({ s: { r: headerStartIndex, c: i }, e: { r: headerStartIndex + 2, c: i } });
      }

      // Merge item group headers horizontally (e.g., 12.5Kg spans 10 cols)
      let currentCol = 8; 
      this.uniqueItemNames.forEach(() => {
        merges.push({ s: { r: headerStartIndex, c: currentCol }, e: { r: headerStartIndex, c: currentCol + 9 } });
        currentCol += 10;
      });

      // Merge Item Price vertically and AUTOMATIC/OTHER/TRUST horizontally
      currentCol = 8;
      this.uniqueItemNames.forEach(() => {
        merges.push({ s: { r: headerStartIndex + 1, c: currentCol }, e: { r: headerStartIndex + 2, c: currentCol } });
        merges.push({ s: { r: headerStartIndex + 1, c: currentCol + 1 }, e: { r: headerStartIndex + 2, c: currentCol + 1 } });
        merges.push({ s: { r: headerStartIndex + 1, c: currentCol + 2 }, e: { r: headerStartIndex + 2, c: currentCol + 2 } });
        merges.push({ s: { r: headerStartIndex + 1, c: currentCol + 3 }, e: { r: headerStartIndex + 2, c: currentCol + 3 } });
        
        merges.push({ s: { r: headerStartIndex + 1, c: currentCol + 4 }, e: { r: headerStartIndex + 1, c: currentCol + 5 } });
        merges.push({ s: { r: headerStartIndex + 1, c: currentCol + 6 }, e: { r: headerStartIndex + 1, c: currentCol + 7 } });
        merges.push({ s: { r: headerStartIndex + 1, c: currentCol + 8 }, e: { r: headerStartIndex + 1, c: currentCol + 9 } });
        currentCol += 10;
      });

      ws['!merges'] = merges;

      // Make title bold
      if (ws['A1']) ws['A1'].s = { font: { bold: true, sz: 14 } };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Stock_Return_Report");
      XLSX.writeFile(wb, `Stock_Return_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
      
      this.toast.success("Excel exported successfully.");
    },

    formatNumber(v) {
      if (!v) return "0.00";
      const n = Number(v);
      if (!Number.isFinite(n)) return "0.00";
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
};
</script>

<style scoped>
.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  width: calc(100% - 20px);
  box-sizing: border-box;
}

.dropdown-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 15px;
  width: 100%;
  margin-bottom: 20px;
  align-items: end;
}

.dropdown-item {
  display: flex;
  flex-direction: column;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
  color: #333;
}

select,
input[type="date"],
input[type="text"] {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
}

select:focus,
input[type="date"]:focus,
input[type="text"]:focus {
  border-color: #007bff;
}

.search-container {
  display: flex;
  justify-content: stretch;
}

.btn {
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.button-default {
  background-color: #007bff;
  color: #ffffff;
}

.button-default:hover {
  background-color: #0069d9;
  color: #ffffff !important;
}

.button-export {
  background-color: #28a745;
  color: #ffffff;
}

.button-export:hover {
  background-color: #218838;
  color: #ffffff !important;
}

.btn:disabled {
  background-color: #6c757d !important;
  color: #ffffff !important;
  cursor: not-allowed;
  opacity: 1;
}

.card {
  border: 1px solid #e3e6f0;
  border-radius: 5px;
  background-color: #fff;
}

.mt-3 {
  margin-top: 1rem;
}

.table-scroll {
  max-height: 500px;
  overflow: auto;
  position: relative;
}

.table {
  width: 100%;
  margin-bottom: 0;
  color: #212529;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 12px;
  border: 1px solid #dee2e6;
  font-size: 14px;
  white-space: nowrap;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.03);
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

.align-middle {
  vertical-align: middle !important;
}

.fw-bold {
  font-weight: 600;
}

.table thead th {
  position: sticky;
  background: #f8f9fc;
  z-index: 5;
  white-space: nowrap;
}

.table thead tr:nth-child(1) th {
  top: 0;
}

.table thead tr:nth-child(2) th {
  top: 45px;
  border-top: none;
}

.table thead tr:nth-child(3) th {
  top: 80px;
  border-top: none;
}

.group-header {
  background-color: #e2e6ea !important;
  border-bottom: 2px solid #ccc !important;
}

.sub-header {
  background-color: #f1f3f5 !important;
  font-size: 13px;
  color: #495057;
}

.auto-header {
  background-color: #e0f2fe !important;
  color: #0369a1 !important;
}
.auto-cell {
  background-color: #f0f9ff !important;
}

.other-header {
  background-color: #fef3c7 !important;
  color: #b45309 !important;
}
.other-cell {
  background-color: #fffbeb !important;
}

.trust-header {
  background-color: #dcfce7 !important;
  color: #15803d !important;
}
.trust-cell {
  background-color: #f0fdf4 !important;
}

.text-end {
  text-align: right !important;
}

.text-center {
  text-align: center !important;
}

.item-divider-left {
  border-left: 2px solid #858796 !important;
}

.loading-spinner {
  font-size: 16px;
  text-align: center;
  padding: 20px;
  color: #007bff;
}

.no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 15px;
  color: #6c757d;
  background-color: #f8f9fc;
  border-radius: 5px;
  margin-top: 20px;
}
</style>